import { createFileRoute } from '@tanstack/react-router';
import type { ReactElement } from 'react';

import { CardAddressView } from './c.$shortLink.{-$slug}';

/**
 * `/c/<ref>/com/<n>` — a card address scoped to one of its comments **by
 * number** (owner-directed 2026-10-13; `docs/notes/comment-numbers.md`).
 * `/c/MID-3245/com/3` opens card `MID-3245` over its board, exactly as
 * `/c/MID-3245` does, and the card back scrolls to comment `#3` and highlights
 * it.
 *
 * ⚠ **The body is `CardAddressView`, the card route's own**, so resolution, the
 * 404/sign-in fallbacks, the board behind the card and the close path cannot
 * drift between the two addresses. What differs is the canonical form: a stale
 * reference or a short link is corrected to `/c/<current ref>/com/<n>`, keeping
 * the tail. Reusing the card route's canonicalisation unchanged would replace
 * this address with the card's slug path and drop the target, with nothing red.
 *
 * ⚠ **The static `com` segment outranks the card route's optional slug**, so
 * `/c/X/com/3` matches here, and `/c/X/com` (no number) still matches the card
 * route with `slug: 'com'` — a slug is cosmetic and never sent. A card slug is
 * always `<n>-<title>`, so no real slug is `com`.
 *
 * A number that is not a positive integer opens the plain card: with no
 * `commentNumber` the canonical form is the card's own path, and the address is
 * corrected to it. A well-formed number with no comment behind it opens the card
 * with nothing highlighted — or with the deleted-comment notice when a later
 * number proves it existed (`CommentSection`).
 */
export const Route = createFileRoute('/c/$shortLink/com/$number')({
  staticData: { layout: 'full-bleed' },
  component: CardCommentNumberRoute,
});

/** `comments.number` is a Postgres `integer` with `ck_comments_number > 0`. */
const MAX_COMMENT_NUMBER = 2_147_483_647;

export function parseCommentNumber(raw: string): number | undefined {
  if (!/^[1-9][0-9]{0,9}$/.test(raw)) return undefined;
  const value = Number(raw);
  return value <= MAX_COMMENT_NUMBER ? value : undefined;
}

function CardCommentNumberRoute(): ReactElement {
  const { shortLink, number } = Route.useParams();
  const matchPathname = Route.useMatch({ select: (match) => match.pathname });
  return (
    <CardAddressView
      shortLink={shortLink}
      matchPathname={matchPathname}
      commentNumber={parseCommentNumber(number)}
    />
  );
}
